/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IButton, IconButton } from '@fluentui/react/lib/Button';
import { Shimmer, ShimmerElementType } from '@fluentui/react/lib/Shimmer';
import { Alignment, IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { ITextProps, ITextStyles } from '@fluentui/react/lib/Text';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { EditableTitleTextField, EditableTitleTextFieldDto } from 'src/core/components/EditableTitleTextField';
import { LuisText, LuisTextProps } from 'src/core/components/LuisText';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';

const messages = defineMessages({
    editIconAriaLabel: {
        id: 'core.pageTitle.editIconAriaLabel',
        defaultMessage: 'Edit title'
    }
});

const { styleComponent } = getStylistV2('EditableTitle');
const scopedTheme = getScopedTheme('EditableTitle');

const Root = styleComponent<IStackProps>(Stack)('Root', {
    marginBottom: 8
});

// Hidden border is added to make the title the same hight as the edit text box
const defaultTitleStyles = classNamesFunction<ITextProps, ITextStyles>()({
    root: { lineHeight: scopedTheme.lineHeight, borderBottom: '1px solid transparent' }
});

export type EditableTitleProps = {
    /**
     * Title to be displayed.
     */
    title: string;
    /**
     * Optional: class name to style the component.
     */
    className?: string;
    /**
     * Optional: text props for the title.
     */
    titleTextProps?: LuisTextProps;
    /**
     * Optional: subtitle.
     */
    subtitle?: string | JSX.Element;
    /**
     * Optional: Indicates if the title is editable.
     */
    isEditable?: boolean;
    /**
     * Optional: Indicates if the title is loading.
     */
    isLoading?: boolean;
    /**
     * Optional: Title description (e.g. docs).
     */
    description?: React.ReactNode;
    /**
     * Optional: Alignment of the description element relative to the title.
     */
    descriptionAlignment?: Alignment;
    /**
     * Optional: Renderer for title element.
     */
    onRenderTitle?: (props: EditableTitleProps, defaultRender: (props: EditableTitleProps) => React.ReactNode) => React.ReactNode;
    /**
     * Optional: callback for when title is updated.
     */
    onUpdateTitle?: (newTitle: string) => void;
    /**
     * Optional: Aria-label for callout button.
     */
    descriptionAriaLabel?: string;
    /**
     * Optional: Data-utomation-id for test.
     */
    dataAutomationId?: string;
};

const defaultRender = ({
    subtitle,
    title,
    description,
    descriptionAlignment = 'baseline',
    titleTextProps,
    descriptionAriaLabel,
    dataAutomationId
}: EditableTitleProps) => (
    <Stack tokens={{ childrenGap: 5 }}>
        <Stack verticalAlign={descriptionAlignment} horizontal tokens={{ childrenGap: 5 }}>
            <LuisText
                as="h1"
                aria-label={title}
                fontWeight={600}
                variant="xLargePlus"
                styles={defaultTitleStyles}
                data-automation-id={dataAutomationId}
                {...titleTextProps}>
                {title}
            </LuisText>
            {description && <CalloutButton content={description} ariaLabel={descriptionAriaLabel} />}
        </Stack>
        {subtitle && <LuisText variant="medium">{subtitle}</LuisText>}
    </Stack>
);

export const EditableTitle = (props: EditableTitleProps) => {
    const { title, isLoading = false, isEditable = false, onUpdateTitle, onRenderTitle, className } = props;

    const { localize } = useLocalization();
    const [isEditMode, setIsEditMode] = React.useState<boolean>(false);
    const [editableTitle, setEditableTitle] = React.useState<string>(title);
    const iconButtonRef = React.useRef<IButton>();

    /**
     * This ensures that when the title is changed back in the
     * event of web api failure, it is reflected in the component.
     */
    React.useEffect(() => setEditableTitle(title), [title]);

    /**
     * Submits the value of the input field to the consumer
     * (parent) component only if the value of has actually
     * been changed than the last time it was edited.
     *
     * @param dto The data received from the text field.
     */
    const submitTitle = (dto: EditableTitleTextFieldDto) => {
        closeEditMode();

        setEditableTitle(dto.value);

        if (onUpdateTitle && dto.changed) {
            onUpdateTitle(dto.value);
        }
    };

    /**
     * Closes edit mode by setting the state flag and refocusing
     * on the edit button.
     */
    const closeEditMode = () => {
        setIsEditMode(false);
        requestAnimationFrame(() => iconButtonRef.current.focus());
    };

    return (
        <Root className={className} horizontal verticalAlign="center" tokens={{ childrenGap: 8 }} data-id="EditableTitle">
            {isLoading ? (
                <Shimmer width={200} shimmerElements={[{ type: ShimmerElementType.line }]} />
            ) : isEditMode ? (
                <EditableTitleTextField initialValue={editableTitle} onSubmit={submitTitle} onCancel={closeEditMode} />
            ) : (
                <>
                    {onRenderTitle ? onRenderTitle(props, defaultRender) : defaultRender({ ...props, title: editableTitle })}
                    {isEditable ? (
                        <LuisTooltip content={localize(messages.editIconAriaLabel)}>
                            <IconButton
                                ariaLabel={localize(messages.editIconAriaLabel)}
                                componentRef={iconButtonRef}
                                onClick={() => setIsEditMode(true)}
                                iconProps={{ iconName: 'Edit' }}
                            />
                        </LuisTooltip>
                    ) : null}
                </>
            )}
        </Root>
    );
};

EditableTitle.displayName = 'PageTitleV2';
